import cx from 'classnames'
import { Variants } from 'motion/react'
import { useRouter } from 'next/router'
import {
  FC,
  ReactElement,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { easeTransition, springTransition } from 'src/hooks/useAppear'
import { useDebounceValue } from 'usehooks-ts'

import { BurgerProps } from '../Burger'
import { HeaderSearchProps } from '../HeaderSearch'
import { LangSwitchProps } from '../LangSwitch'
import { LangSwitchPosition } from '../LangSwitch/types'
import { LinkProps } from '../Link'
import { LogoProps } from '../Logo'
import { MenuProps } from '../Menu'

import * as SC from './styled'
import { Scrolled } from './types'

type HeaderMenuItemProps = {
  condition: boolean
  variants: Variants
  children: ReactElement
}

export type HeaderProps = {
  className?: string
  id?: string
  logoProps?: LogoProps
  langSwitchProps?: LangSwitchProps
  burgerProps?: BurgerProps
  menuProps?: MenuProps
  externalLink?: LinkProps
  searchProps?: HeaderSearchProps
  refMenu?: RefObject<HTMLDivElement | null>
}

const HeaderMenuItem: FC<HeaderMenuItemProps> = ({
  condition,
  variants,
  children,
}) => {
  if (condition) {
    return <SC.MenuWrapper variants={variants}>{children}</SC.MenuWrapper>
  }
  return children
}

const Header: FC<HeaderProps> = ({
  className,
  id = 'Header',
  logoProps,
  langSwitchProps,
  burgerProps,
  menuProps,
  externalLink,
  searchProps,
  refMenu,
}) => {
  const [scrollPosition, setScrollPosition] = useDebounceValue(0, 50)
  const [scrolled, setScrolled] = useState<Scrolled>(Scrolled.Default)
  const ref = useRef<HTMLHeadElement>(null)

  const { isHome, hasAppeared, animations, variants } = useHeaderMotion()

  // Change stuff on scroll
  useEffect(() => {
    const handleScroll = () => {
      // console.log('scrolling', window.scrollY, scrollPosition)
      if (window.scrollY >= 50 && window.scrollY < 200) {
        // console.log('scroll -> reduced')
        setScrolled(Scrolled.Reduced)
      } else if (window.scrollY >= 200 && window.scrollY > scrollPosition) {
        // console.log('scroll -> down')
        setScrolled(Scrolled.Down)
      } else if (window.scrollY >= 200 && window.scrollY < scrollPosition) {
        // console.log('scroll -> up')
        setScrolled(Scrolled.Up)
      } else {
        // console.log('scroll -> default')
        setScrolled(Scrolled.Default)
      }
      setScrollPosition(window.scrollY)
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrollPosition, setScrollPosition])

  const _Logo = isHome ? SC._MotionLogo : SC._Logo

  return (
    <>
      <SC.Container
        className={cx('Header', className)}
        ref={ref}
        id={id}
        data-scrolled={scrolled}
        {...(menuProps?.open && { 'data-open': '' })}
      >
        <SC.Left {...(isHome ? animations.left : null)}>
          {logoProps && <_Logo {...logoProps} />}
        </SC.Left>
        <SC.Right {...(isHome || !hasAppeared ? animations.right : null)}>
          {langSwitchProps && (
            <HeaderMenuItem
              condition={isHome || !hasAppeared}
              variants={variants.menu}
            >
              <SC.DesktopLang {...langSwitchProps} />
            </HeaderMenuItem>
          )}
          {searchProps && (
            <HeaderMenuItem
              condition={isHome || !hasAppeared}
              variants={variants.menu}
            >
              <SC._HeaderSearch {...searchProps} />
            </HeaderMenuItem>
          )}
          <SC.Separator aria-hidden />
          {externalLink && (
            <HeaderMenuItem
              condition={isHome || !hasAppeared}
              variants={variants.menu}
            >
              <SC.DesktopExternal {...externalLink} />
            </HeaderMenuItem>
          )}
          <HeaderMenuItem
            condition={isHome || !hasAppeared}
            variants={variants.menu}
          >
            <SC._Burger {...burgerProps} />
          </HeaderMenuItem>
        </SC.Right>
      </SC.Container>
      <SC._Menu {...menuProps} ref={refMenu} />
      {/* appears only on mobile at the BOTTOM of the screen */}
      <SC.Bottom>
        {langSwitchProps && (
          <SC.MobileLang
            {...langSwitchProps}
            position={LangSwitchPosition.bottom}
          />
        )}
        {externalLink && <SC.MobileExternal {...externalLink} />}
      </SC.Bottom>
      <SC._Overlay hidden={!menuProps?.open} aria-hidden />
    </>
  )
}

const useHeaderMotion = () => {
  const router = useRouter()
  const { hasAppeared, setHasAppearedStorage } = useHeaderStorage()

  const isHome = router.pathname === '/'

  const transitions = useMemo(
    () => ({
      left: { ...easeTransition, duration: 1.3, delay: 2.2 },
      right: {
        ...springTransition,
        staggerChildren: 0.1,
        delayChildren: isHome ? 3.25 : 1.25,
      },
    }),
    [isHome]
  )

  const animations = useMemo(
    () => ({
      left: {
        initial: { scale: 2 },
        animate: { scale: 1 },
        transition: transitions.left,
      },
      right: {
        initial: 'hidden',
        animate: 'visible',
        variants: { visible: { transition: transitions.right } },
        onAnimationComplete: () => {
          setHasAppearedStorage(true)
        },
      },
    }),
    [transitions, setHasAppearedStorage]
  )

  const variants = useMemo(
    () => ({
      menu: {
        hidden: { opacity: 0, y: '-20%' },
        visible: { opacity: 1, y: 0 },
      },
    }),
    []
  )

  return { isHome, hasAppeared, animations, variants }
}

const useHeaderStorage = () => {
  const [hasAppeared, setHasAppeared] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const storedValue = window.sessionStorage.getItem('hasAppeared')

    setHasAppeared(storedValue === 'true')
  }, [])

  const setHasAppearedStorage = (value: boolean) => {
    if (typeof window === 'undefined') return

    window.sessionStorage.setItem('hasAppeared', value.toString())
  }

  return { hasAppeared, setHasAppearedStorage }
}

export default Header
